import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import "../assets/css/Features.css";

// Images
import OptimizationImg from "../assets/img/features/ai-optimization.png";
import AnalyticsImg from "../assets/img/features/analytics-dashboard.png";
import CrossPlatformImg from "../assets/img/features/cross-platform-analytics.png";
import ReportingImg from "../assets/img/features/automated-reporting.png";
import BenchmarkingImg from "../assets/img/features/competitor-benchmarking.png";

const features = [
  {
    id: "analytics",
    title: "Cross-Platform Analytics",
    description: "Unified view of your performance across Facebook, Instagram, and YouTube. Track reach, impressions, and engagement in one dashboard.",
    image: CrossPlatformImg
  },
  {
    id: "benchmarking",
    title: "Competitor Benchmarking",
    description: "Stay ahead by comparing your growth and strategy directly against key rivals on Instagram, YouTube, and Facebook.",
    image: BenchmarkingImg
  },
  {
    id: "reporting",
    title: "Automated Reporting",
    description: "Save hours with scheduled PDF snapshots. Create Market, Brand, or Campaign reports that land in your inbox automatically.",
    image: ReportingImg
  },
  {
    id: "optimization",
    title: "AI Growth Intelligence",
    description: "Stop guessing. Use AI to know the optimal time to post, the best content formats, and the right frequency for maximum reach.",
    image: OptimizationImg
  }
];

const Card = ({ feature, index, total, progress }) => {
  // Logic: Step-Based Scaling
  // We want exact synchronization: 
  // Card scales down ONLY when the NEXT card is in its arrival phase.

  const width = 1 / total;

  // Y Animation (Arrival)
  // Card i arrives during [(index - 1) * width, index * width]
  const arrivalStart = (index - 1) * width;
  const arrivalEnd = index * width;

  // Standard linear transform for Y, smoothed by the parent's spring
  const y = useTransform(
    progress,
    [arrivalStart, arrivalEnd],
    ["110vh", "0vh"]
  );

  // Scale Animation (dynamic arrays)
  // We define keyframes at every "width" interval.
  const inputRange = [];
  const outputScale = [];

  for (let i = 0; i <= total; i++) {
    const point = i * width;
    inputRange.push(point);

    // Calculate scale at this progress point
    if (point <= index * width) {
      // Current card has not finished arriving yet. Full scale.
      outputScale.push(1);
    } else {
      // Successive cards are arriving.
      const depth = i - index;
      const scale = 1 - (depth * 0.1);
      outputScale.push(Math.max(0, scale));
    }
  }

  const scale = useTransform(progress, inputRange, outputScale);

  return (
    <motion.div
      className="scroll-card-absolute"
      style={{
        y: index === 0 ? 0 : y, // Card 0 fixed Y (unless we want it to move? No, sticky base)
        scale,
        zIndex: index
      }}
    >
      <div className="card-image-container">
        <img src={feature.image} alt={feature.title} />
      </div>
      <div className="card-text-content">
        <h3 className="card-title">{feature.title}</h3>
        <p className="card-description">{feature.description}</p>
      </div>
    </motion.div>
  );
};

const Features = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // Apply physics smoothing
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 200, // Faster response
    damping: 40,   // More controlled movement
    restDelta: 0.001
  });

  return (
    <section id="features" className="image-scroll-section">
      <div className="image-scroll-track" ref={containerRef}>
        <div className="image-scroll-sticky-viewport">
          <div className="container text-center mb-2">
            <h2 className="section-title">Master Your Growth and Outperform the Competition</h2>
            <p className="section-subtitle">Powerful tools to analyse, benchmark and grow your presence.</p>
          </div>
          <div className="scroll-card-stack">
            {features.map((feature, index) => (
              <Card
                key={index}
                feature={feature}
                index={index}
                total={features.length}
                progress={smoothProgress}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
